import axios from 'axios'
import moment from 'moment'
import 'moment/locale/es'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { constants } from '../Constants'
import { PageLoader } from '../components/ui/Spinner/Spinner'
import './ClassesCalendar.scss'

moment.locale('es')

const WEEKDAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D']

export const ClassesCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(
    moment.utc().startOf('month')
  )
  const [selectedDate, setSelectedDate] = useState(
    moment.utc().format('YYYY-MM-DD')
  )
  const [eventsByDay, setEventsByDay] = useState({})
  const [loading, setLoading] = useState(true)

  const year = currentMonth.year()
  const month = currentMonth.month() + 1

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true)
      try {
        const res = await axios.get(`${constants.urlLocal}events/byMonth`, {
          params: { year, month },
        })
        if (res.data.ok) {
          setEventsByDay(res.data.grouped || {})
        }
      } catch (err) {
        console.error('Error al cargar el horario:', err)
        setEventsByDay({})
      }
      setLoading(false)
    }
    fetchEvents()
  }, [year, month])

  // Rejilla completa lunes→domingo para el mes actual
  const calendarDays = useMemo(() => {
    const start = moment(currentMonth).startOf('month').startOf('isoWeek')
    const end = moment(currentMonth).endOf('month').endOf('isoWeek')
    const days = []
    const cursor = moment(start)
    while (cursor.isSameOrBefore(end, 'day')) {
      days.push(moment(cursor))
      cursor.add(1, 'day')
    }
    return days
  }, [currentMonth])

  const todayKey = moment.utc().format('YYYY-MM-DD')
  const selectedEvents = eventsByDay[selectedDate] || []

  const formatTime = dateStr => {
    const d = new Date(dateStr)
    return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
  }

  // Colores únicos de eventos del día seleccionado (para dots en la rejilla)
  const getDayColors = dayKey => {
    const events = eventsByDay[dayKey] || []
    const seen = new Set()
    return events
      .map(e => e.color || '#2440ab')
      .filter(c => {
        if (seen.has(c)) return false
        seen.add(c)
        return true
      })
      .slice(0, 3)
  }

  return (
    <div className="page-container clscal">
      <h2 className="page-title" style={{ marginBottom: '0.25rem' }}>
        Horario de Clases
      </h2>
      <p className="page-subtitle" style={{ marginTop: 0 }}>
        Todas las clases disponibles este mes
      </p>

      {/* Calendario */}
      <div className="clscal__card">
        <div className="clscal__nav">
          <button
            className="clscal__nav-btn"
            onClick={() =>
              setCurrentMonth(moment(currentMonth).subtract(1, 'month'))
            }
            aria-label="Mes anterior"
          >
            ‹
          </button>
          <div className="clscal__nav-title">
            <strong>{currentMonth.format('MMMM')}</strong>{' '}
            <span>{currentMonth.format('YYYY')}</span>
          </div>
          <button
            className="clscal__nav-btn"
            onClick={() =>
              setCurrentMonth(moment(currentMonth).add(1, 'month'))
            }
            aria-label="Mes siguiente"
          >
            ›
          </button>
        </div>

        <button
          className="clscal__today-btn"
          onClick={() => {
            setCurrentMonth(moment.utc().startOf('month'))
            setSelectedDate(todayKey)
          }}
        >
          Hoy
        </button>

        <div className="clscal__weekdays">
          {WEEKDAYS.map(d => (
            <span key={d}>{d}</span>
          ))}
        </div>

        {loading ? (
          <div className="clscal__grid-loading">
            <PageLoader text="Cargando clases..." />
          </div>
        ) : (
          <div className="clscal__grid">
            {calendarDays.map(day => {
              const key = day.format('YYYY-MM-DD')
              const colors = getDayColors(key)
              const hasEvents = colors.length > 0
              const isCurrentMonth = day.isSame(currentMonth, 'month')
              const isToday = key === todayKey
              const isSelected = key === selectedDate

              return (
                <button
                  key={key}
                  className={[
                    'clscal__day',
                    !isCurrentMonth && 'clscal__day--muted',
                    isToday && 'clscal__day--today',
                    isSelected && 'clscal__day--selected',
                    hasEvents && 'clscal__day--has',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setSelectedDate(key)}
                >
                  <span className="clscal__day-num">{day.format('D')}</span>
                  {hasEvents && (
                    <span className="clscal__dots">
                      {colors.map((color, i) => (
                        <span
                          key={i}
                          className="clscal__dot"
                          style={{ background: color }}
                        />
                      ))}
                      {(eventsByDay[key] || []).length > 3 && (
                        <span className="clscal__more">
                          +{(eventsByDay[key] || []).length - 3}
                        </span>
                      )}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}

        <div className="clscal__legend">
          <span>
            <i className="clscal__lg clscal__lg--today" /> Hoy
          </span>
          <span>
            <i className="clscal__lg clscal__lg--has" /> Con clases
          </span>
        </div>
      </div>

      {/* Detalle del día seleccionado */}
      <div className="clscal__card">
        <h3 className="clscal__detail-title">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          {moment.utc(selectedDate).format('dddd, D [de] MMMM')}
        </h3>

        {loading ? (
          <p className="clscal__empty">Cargando…</p>
        ) : selectedEvents.length === 0 ? (
          <p className="clscal__empty">No hay clases este día.</p>
        ) : (
          <div className="clscal__detail-list">
            {selectedEvents.map(ev => (
              <Link
                to={`/event/${ev._id}`}
                key={ev._id}
                className="clscal__item"
                style={{ borderLeftColor: ev.color || '#2440ab' }}
              >
                <div
                  className="clscal__item-color"
                  style={{ background: ev.color || '#2440ab' }}
                />
                <div className="clscal__item-body">
                  <span className="clscal__item-name">
                    {ev.categoryName || ev.title || 'Clase'}
                  </span>
                  <span className="clscal__item-time">
                    {formatTime(ev.start)}
                    {ev.end && ` — ${formatTime(ev.end)}`}
                  </span>
                </div>
                <svg
                  className="clscal__item-arrow"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
